import { Flex, Text, UnstyledButton, type Action } from "@wayflyer/flyui";
import { useMemo } from "react";
import { styled } from "styled-components";

export const AddressDisplayContainer = styled(Flex)`
  border: 1px solid var(--palette-border-neutral-subtle);
  color: var(--palette-content-neutral-subtle);
  background-color: var(--palette-bg-neutral-subtle);
  border-radius: var(--sizes-radius-md);
`;

export const AddressDisplayButton = styled(UnstyledButton)`
  padding-top: var(--sizes-spacing-2);
  padding-bottom: var(--sizes-spacing-1);
  color: var(--palette-content-accent-strong);
  font-weight: 600;
`;

interface AddressFields {
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

interface AddressDisplayProps {
  title: string;
  action: Action;
  address?: string[];
  addressFields?: AddressFields;
}

const formatAddressFields = (fields: AddressFields): string[] => {
  const { addressLine1, addressLine2, city, state, zipCode } = fields;

  const addressLines: string[] = [];

  if (addressLine1) {
    addressLines.push(addressLine1.trim());
  }
  if (addressLine2) {
    addressLines.push(addressLine2.trim());
  }

  const stateZip = [state, zipCode].filter(Boolean).join(" ");
  const cityStateZip = [city, stateZip].filter(Boolean).join(", ");
  addressLines.push(cityStateZip);

  return addressLines;
};

export const AddressDisplay = ({ address, addressFields, title, action }: AddressDisplayProps) => {
  const { onAction, content, disabled } = action;

  const formattedAddress = useMemo(() => {
    if (address) {
      return address;
    }
    if (addressFields) {
      return formatAddressFields(addressFields);
    }
    return [];
  }, [address, addressFields]);

  if (formattedAddress.length === 0) {
    return null;
  }

  return (
    <AddressDisplayContainer padding={["3", "3", "2", "3"]} data-testid="address-display-container">
      <Flex direction="column" gap="0" width="100%">
        <Text data-testid="address-display-title" fontWeight="semiBold">
          {title}
        </Text>
        {formattedAddress.map((line, index) => (
          <Text data-testid="address-display-line" lineHeight="tight" key={index}>
            {line}
          </Text>
        ))}
        <Flex justify="flex-start" padding={["0", "0", "0", "0"]}>
          <AddressDisplayButton data-testid="address-display-button" disabled={disabled} onClick={onAction}>
            {content}
          </AddressDisplayButton>
        </Flex>
      </Flex>
    </AddressDisplayContainer>
  );
};
